import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { MapPin, Navigation, Shield } from 'lucide-react';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const LIBRARIES: ('places')[] = ['places'];

const STATUS_LABEL: Record<string, string> = {
  pending: 'Finding a driver…',
  accepted: 'Driver is on the way',
  arrived: 'Driver has arrived',
  in_progress: 'On the trip',
  completed: 'Trip completed',
  cancelled: 'Trip cancelled',
  rejected: 'Trip cancelled',
};

// Live vehicle marker - same gold arrow (rotated to heading) the customer and
// driver maps use, so the shared tracker reads identically. Only built while the
// map is loaded, so window.google is available.
const liveDriverIcon = (h: number) => ({
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">` +
    `<g transform="rotate(${h},24,24)">` +
    `<circle cx="24" cy="24" r="22" fill="#F5A623" stroke="white" stroke-width="3"/>` +
    `<polygon points="24,7 33,36 24,30 15,36" fill="white"/>` +
    `</g></svg>`
  ),
  scaledSize: new google.maps.Size(48, 48),
  anchor: new google.maps.Point(24, 24),
});

// Green (pickup) / red (dropoff) map pins, matching the in-app trip maps.
const pinIcon = (color: string) => ({
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">` +
    `<path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 26 18 26S36 31.5 36 18C36 8.06 27.94 0 18 0z" fill="${color}"/>` +
    `<circle cx="18" cy="18" r="8" fill="white"/></svg>`
  ),
  scaledSize: new google.maps.Size(36, 44),
  anchor: new google.maps.Point(18, 44),
});

// Public, no-login live trip view shared via "Share my trip". Polls the public
// endpoint so a friend/family member can watch the ride in real time.
const TripTracking: React.FC = () => {
  const { rideId } = useParams<{ rideId: string }>();
  const [trip, setTrip] = useState<any>(null);
  const [notFound, setNotFound] = useState(false);
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_KEY, libraries: LIBRARIES as any });

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/public/trip/${rideId}`);
      if (res.status === 404) { setNotFound(true); return; }
      const data = await res.json();
      setTrip(data);
    } catch {}
  }, [rideId]);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 8000);
    return () => clearInterval(t);
  }, [load]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-safaricab-darker flex flex-col items-center justify-center px-6 text-center">
        <MapPin className="w-12 h-12 text-white/20 mb-4" />
        <h1 className="text-white font-bold text-xl mb-1">Trip not found</h1>
        <p className="text-white/50 text-sm">This tracking link is invalid or has expired.</p>
      </div>
    );
  }
  if (!trip) {
    return (
      <div className="min-h-screen bg-safaricab-darker flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-safaricab-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const center = trip.location || trip.pickup || { lat: -1.286389, lng: 36.817223 };
  const ended = trip.status === 'completed' || trip.status === 'cancelled' || trip.status === 'rejected';

  return (
    <div className="min-h-screen bg-safaricab-darker flex flex-col">
      <div className="px-4 pt-[calc(max(var(--sai-top),14px)+1rem)] pb-3 flex items-center gap-2 bg-safaricab-dark">
        <Shield className="w-5 h-5 text-safaricab-gold" />
        <span className="text-white font-bold text-sm">SafariCab · Live trip</span>
      </div>

      <div className="relative w-full" style={{ height: '55vh' }}>
        {isLoaded && GOOGLE_KEY ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center as any}
            zoom={15}
            options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy', styles: [{ stylers: [{ saturation: -50 }] }] }}
          >
            {trip.pickup?.lat && <Marker position={{ lat: trip.pickup.lat, lng: trip.pickup.lng }} icon={pinIcon('#22c55e')} title="Pickup" />}
            {trip.dropoff?.lat && <Marker position={{ lat: trip.dropoff.lat, lng: trip.dropoff.lng }} icon={pinIcon('#ef4444')} title="Dropoff" />}
            {trip.location && <Marker position={{ lat: trip.location.lat, lng: trip.location.lng }} icon={liveDriverIcon(trip.location.heading ?? 0)} zIndex={999} title="Driver" />}
          </GoogleMap>
        ) : (
          <div className="w-full h-full bg-safaricab-dark flex items-center justify-center text-white/30 text-sm">Map loading…</div>
        )}
      </div>

      <div className="bg-safaricab-dark rounded-t-3xl -mt-4 px-4 pt-4 pb-[calc(var(--sai-bottom)+1.5rem)] relative z-10">
        <span className={`inline-block text-xs font-bold px-3 py-1 rounded-full mb-3 ${ended ? 'bg-white/10 text-white/60' : 'bg-safaricab-gold/20 text-safaricab-gold'}`}>
          {STATUS_LABEL[trip.status] || trip.status}
        </span>

        {trip.driver && (
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-safaricab-gold/20 flex items-center justify-center text-safaricab-gold font-bold">
              {trip.driver.name?.charAt(0) || 'D'}
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm">{trip.driver.name}</p>
              {trip.vehicle && (
                <p className="text-white/50 text-xs">
                  {[trip.vehicle.make, trip.vehicle.color].filter(Boolean).join(' · ')}
                  {trip.vehicle.plate_number ? ` · ${trip.vehicle.plate_number}` : ''}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-green-500 flex-shrink-0" /><span className="text-white/80 truncate">{trip.pickup?.address || '-'}</span></div>
          <div className="flex items-center gap-2 text-sm"><Navigation className="w-4 h-4 text-red-500 flex-shrink-0" /><span className="text-white/80 truncate">{trip.dropoff?.address || '-'}</span></div>
        </div>

        <p className="text-white/30 text-[11px] text-center mt-4">You're following this trip on SafariCab. Updates automatically.</p>
      </div>
    </div>
  );
};

export default TripTracking;
